import React from "react";
import { Modal, Form, Select, Switch, Button, InputNumber } from "antd";
import { useLocations } from "../../hooks/useLocation";
import { useCreateTable } from "../../hooks/useTables";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../context/PermissionContext";
import { PERMISSIONS } from "../../utils/permissions";
import { message } from "antd";

interface AddTableModalProps {
  visible: boolean;
  onCancel: () => void;
}

const AddTableModal: React.FC<AddTableModalProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const { data: user } = useAuth();
  const { hasPermission } = usePermissions();

  // Get user's location and permission
  const userLocationId = user?.location?.id;
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Fetch all locations for the dropdown
  const { data: locations, isLoading: locationsLoading } = useLocations();

  // Create table mutation
  const { mutate: createTable, isPending: creating } = useCreateTable();

  // Set initial values when modal opens and reset when modal closes
  React.useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        location_id: canSeeAllLocations ? undefined : userLocationId,
        is_booked: false,
        quantity: 1,
      });
    } else {
      // Reset form when modal closes
      form.resetFields();
    }
  }, [visible, canSeeAllLocations, userLocationId, form]);

  const handleFinish = async (values: any) => {
    const { location_id, is_booked, quantity } = values;

    if (quantity > 10) {
      message.error("Maximum 10 tables can be created at once");
      return;
    }

    try {
      // Create tables sequentially to avoid mutation conflicts
      for (let i = 0; i < quantity; i++) {
        await new Promise<void>((resolve, reject) => {
          createTable(
            {
              location_id: location_id,
              is_booked: is_booked || false,
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error),
            }
          );
        });
      }

      message.success(`${quantity} table(s) created successfully!`);
      form.resetFields();
      onCancel();
    } catch (error) {
      console.error("Failed to create tables:", error);
      message.error("Failed to create some tables. Please try again.");
    }
  };

  return (
    <Modal
      title="Add New Table(s)"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          location_id: canSeeAllLocations ? undefined : userLocationId,
          is_booked: false,
          quantity: 1,
        }}
      >
        {/* Number of Tables */}
        <Form.Item
          label="Number of Tables"
          name="quantity"
          rules={[
            { required: true, message: "Please specify number of tables" },
            {
              type: "number",
              min: 1,
              max: 10,
              message: "Must be between 1 and 10",
            },
          ]}
        >
          <InputNumber
            min={1}
            max={10}
            placeholder="Enter number of tables (max 10)"
            style={{ width: "100%" }}
          />
        </Form.Item>

        {/* Location Dropdown */}
        <Form.Item
          label="Location"
          name="location_id"
          rules={[{ required: true, message: "Please select a location" }]}
        >
          <Select
            placeholder="Select a location"
            loading={locationsLoading}
            allowClear={canSeeAllLocations}
            disabled={!canSeeAllLocations}
          >
            {locations?.map((loc) => (
              <Select.Option key={loc.id} value={loc.id}>
                {loc.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Is Booked (Toggle) */}
        <Form.Item
          label="Is Booked?"
          name="is_booked"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {/* Modal Footer Buttons */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={creating}
            style={{ marginRight: 8 }}
          >
            Create
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddTableModal;
